import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Team & HQ galleries render via FrameTile / HqBanner below.

function FrameTile({ src, caption, inView, className = '', aspect = 'aspect-[4/3] md:aspect-auto md:h-full', objPos = 'object-center' }) {
  return (
    <motion.figure
      initial={{ opacity: 0 }}
      animate={inView ? { opacity: 1 } : {}}
      transition={{ duration: 0.55, ease: 'easeOut' }}
      className={`group relative m-0 overflow-hidden rounded-lg border border-white/10 bg-coal ${aspect} ${className}`}
      data-testid="hq-frame-tile"
    >
      <img
        src={src}
        alt={caption}
        className={`block w-full h-full object-cover ${objPos} grayscale-[0.2] contrast-[1.02] transition-all duration-[800ms] ease-out group-hover:grayscale-0 group-hover:scale-[1.04]`}
        loading="lazy"
      />
      <span className="pointer-events-none absolute top-0 left-0 z-[2] h-[3px] w-0 bg-[hsl(var(--water-blue-2))] transition-all duration-500 group-hover:w-full" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] bg-gradient-to-t from-[#0e1416] via-[#0e1416]/25 to-transparent pt-10">
        <figcaption className="p-3">
          <span className="font-mono text-[9px] sm:text-[9.5px] tracking-[0.14em] uppercase text-bone/90">
            {caption}
          </span>
        </figcaption>
      </div>
    </motion.figure>
  );
}

function HqBanner({ src, caption, inView, aspect = '', testid, natural = false }) {
  return (
    <motion.figure
      initial={{ opacity: 0 }}
      animate={inView ? { opacity: 1 } : {}}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`group relative m-0 overflow-hidden rounded-xl border border-white/10 bg-coal ${natural ? '' : aspect}`}
      data-testid={testid}
    >
      <img
        src={src}
        alt={caption}
        className={`block w-full ${natural ? 'h-auto' : 'h-full object-cover'} grayscale-[0.15] contrast-[1.02] transition-all duration-[800ms] ease-out group-hover:grayscale-0 group-hover:scale-[1.03]`}
        loading="lazy"
      />
      <span className="pointer-events-none absolute top-0 left-0 z-[2] h-[3px] w-0 bg-[hsl(var(--water-blue-2))] transition-all duration-500 group-hover:w-full" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] bg-gradient-to-t from-[#0e1416] via-[#0e1416]/25 to-transparent pt-12">
        <figcaption className="p-3.5">
          <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.16em] uppercase text-bone/90">
            {caption}
          </span>
        </figcaption>
      </div>
    </motion.figure>
  );
}

function GroupHeading({ kicker, title, index, desc }) {
  return (
    <div className="mb-6 border-b border-white/10 pb-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="kicker kicker-on-dark mb-2">{kicker}</p>
          <h3 className="font-display font-semibold text-2xl sm:text-3xl text-bone tracking-tight">{title}</h3>
        </div>
        <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-bone/35 whitespace-nowrap shrink-0">{index}</span>
      </div>
      {desc ? <p className="text-bone/60 text-sm mt-3 max-w-2xl leading-relaxed">{desc}</p> : null}
    </div>
  );
}

export default function CrewSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.03 });

  return (
    <section
      id="crew"
      className="relative py-20 sm:py-24 lg:py-28 bg-coal text-bone overflow-hidden"
      data-testid="crew-section"
      ref={ref}
    >
      <div className="absolute inset-0 bg-grain opacity-[0.05] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-14 max-w-3xl"
        >
          <p className="kicker kicker-on-dark mb-4">The people</p>
          <h2 className="editorial-headline text-4xl sm:text-5xl lg:text-6xl text-bone mb-5" data-testid="crew-headline">
            Office and field,
            <span className="italic font-normal text-forest-2"> one team.</span>
          </h2>
          <p className="text-bone/70 max-w-2xl leading-[1.75] text-[15px]">
            From our administrative team in Conway to the superintendents and field crews
            on each job site, every OMC project is planned, coordinated, and built by the
            same accountable team. Our own crews carry the work from layout to concrete
            placement, giving owners and engineers one clear point of responsibility from
            start to finish.
          </p>
        </motion.div>

        {/* 1) INSIDE OMC HQ */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mb-16"
          data-testid="office-crew-block"
        >
          <GroupHeading
            kicker="Around the HQ"
            title="Ozark Mtn Construction HQ"
            index="01 / HQ"
          />

          {/* HQ — big feature centered; top banner, side tiles, big Main Lobby below */}
          <div className="space-y-3" data-testid="office-gallery">
            {/* Top banner — The Approach */}
            <HqBanner
              src="/team/hq-approach-drive.jpg"
              caption="1002 Artis Lane"
              inView={inView}
              aspect="aspect-[16/7] md:aspect-[16/4]"
              testid="hq-banner-top"
            />

            {/* Reviewing plans at the HQ desk — pond view (client-supplied) */}
            <HqBanner
              src="/team/pelley-32.jpg"
              caption="Reviewing the Plans"
              inView={inView}
              aspect="aspect-[4/3] sm:aspect-[16/9]"
              testid="hq-banner-plans"
            />

            {/* Middle — side tiles flanking the big Conway HQ feature */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:grid-rows-2 md:aspect-[2/1]">
              <FrameTile src="/team/hq-lobby-lounge.jpg" caption="Reception Lounge" inView={inView} className="md:col-start-1 md:row-start-1" />
              <FrameTile src="/team/hq-plans-rack.jpg" caption="Active Project Plans" inView={inView} className="md:col-start-4 md:row-start-1" />

              {/* Big center feature — Conway headquarters building */}
              <motion.figure
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="group relative m-0 overflow-hidden rounded-xl border border-white/12 bg-coal shadow-elev-2 col-span-2 aspect-[4/3] md:aspect-auto md:h-full md:col-start-2 md:col-span-2 md:row-start-1 md:row-span-2"
                data-testid="hq-feature"
              >
                <img
                  src="/team/office-building.jpg"
                  alt="Ozark Mountain Construction headquarters in Conway, Arkansas"
                  className="block w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                  data-testid="hq-feature-img"
                />
                <span className="pointer-events-none absolute top-0 left-0 z-[2] h-[3px] w-0 bg-[hsl(var(--water-blue-2))] transition-all duration-500 group-hover:w-full" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] bg-gradient-to-t from-[#0e1416] via-[#0e1416]/30 to-transparent pt-14">
                  <figcaption className="p-4">
                    <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-bone/95">
                      Conway Headquarters
                    </span>
                  </figcaption>
                </div>
              </motion.figure>

              <FrameTile src="/team/pelley-106.jpg" caption="Project Records" inView={inView} className="md:col-start-1 md:row-start-2" />
              <FrameTile src="/team/hq-plan-storage.jpg" caption="Plan Archive" inView={inView} className="md:col-start-4 md:row-start-2" />
            </div>

            {/* Bottom — big Main Lobby */}
            <HqBanner
              src="/team/office-lobby.jpg"
              caption="Main Lobby"
              inView={inView}
              aspect="aspect-[16/9] md:aspect-[16/6]"
              testid="hq-banner-bottom"
            />
          </div>
        </motion.div>

        {/* 2) OUR TEAM — even tiles wrapping the admin-women feature (matches HQ) */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="field-crew-block"
        >
          <GroupHeading
            kicker="Around the shop"
            title="Our Team"
            index="02 / Team"
            desc="An assortment of the people who make Ozark Mountain Construction — friends, family, and coworkers, around the shop."
          />

          {/* Top banner — the whole crew (natural aspect, compact width) */}
          <div className="max-w-2xl mx-auto">
            <HqBanner
              src="/team/team-crew-group.jpg"
              caption="The OMC Crew"
              inView={inView}
              natural
              testid="team-banner-top"
            />
          </div>

          {/* Middle — even tiles flanking the big admin-women feature */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:grid-rows-2 md:aspect-[2/1] mt-3">
            <FrameTile src="/team/team-crew-69.jpg" caption="Father & Son Duo" inView={inView} className="md:col-start-1 md:row-start-1" objPos="object-[50%_20%]" />
            <FrameTile src="/team/team-crew-80.jpg" caption="Foreman Brothers — Cole & Chase" inView={inView} className="md:col-start-4 md:row-start-1" objPos="object-[50%_15%]" />

            {/* Feature — Manuel, Bryan, Scott & Mark (leadership) */}
            <motion.figure
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="group relative m-0 overflow-hidden rounded-xl border border-white/12 bg-coal shadow-elev-2 col-span-2 aspect-square md:aspect-auto md:h-full md:col-start-2 md:col-span-2 md:row-start-1 md:row-span-2"
              data-testid="field-team-feature"
            >
              <img
                src="/team/team-leadership-4.jpg"
                alt="Manuel, Bryan, Scott and Mark — OMC leadership"
                className="block w-full h-full object-contain transition-transform duration-[800ms] ease-out group-hover:scale-[1.03]"
                loading="lazy"
                data-testid="field-team-feature-img"
              />
              <span className="pointer-events-none absolute top-0 left-0 z-[2] h-[3px] w-0 bg-[hsl(var(--water-blue-2))] transition-all duration-500 group-hover:w-full" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] bg-gradient-to-t from-[#0e1416] via-[#0e1416]/30 to-transparent pt-14">
                <figcaption className="p-4">
                  <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-bone/95">
                    Manuel, Bryan, Scott &amp; Mark
                  </span>
                </figcaption>
              </div>
            </motion.figure>

            <FrameTile src="/team/team-crew-70.jpg" caption="Senior Foreman — Bryan & Blake, Father & Son" inView={inView} className="md:col-start-1 md:row-start-2" objPos="object-[50%_20%]" />
            <FrameTile src="/team/team-crew-73.jpg" caption="Family" inView={inView} className="md:col-start-4 md:row-start-2" objPos="object-[50%_20%]" />
          </div>

          {/* Bottom — leadership four + a plan-review shot, small tiles like the rest */}
          <div className="grid grid-cols-2 gap-3 mt-3 max-w-2xl mx-auto">
            <FrameTile src="/team/admin-women.jpg" caption="The Admin Team · Conway, AR" inView={inView} aspect="aspect-[4/3]" objPos="object-center" />
            <FrameTile src="/team/team-plan-review-56.jpg" caption="Going Over the Plans" inView={inView} aspect="aspect-[4/3]" objPos="object-[50%_35%]" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
